import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MagazziniService} from '../../../_services/magazzini.service';
import {MagazzinoLocale} from '../../../_models/MagazzinoLocale';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {SearchResult} from '../../../_models/SearchResult';
import {SaveMessage} from '../../../_helpers/save-message';
import {MagazzinoAreaLocale} from '../../../_models/MagazzinoAreaLocale';

/**
 * Selettore di magazzino e area, montato in 28 istanze su 16 componenti.
 *
 * Feature 118 (issue #3354): la sorgente passa dalla lettura diretta del gestionale
 * all'ANAGRAFICA LOCALE dell'organizzazione. Il contratto pubblico — `@Input codMag /
 * codAreaMag / tipoMagazzino`, `@Output codMagChange / codAreaMagChange` — resta INVARIATO:
 * e' quell'invarianza che rende la modifica invisibile a tutti i componenti che lo montano.
 * L'adattamento dei nomi di campo (`codice` ⇄ `codMag`, `descrizione` ⇄ `desMagazzino`) vive
 * qui dentro e non esce.
 *
 * L'endpoint letto e' `Magazzini/Selezionabili`, non quello della pagina di gestione: riproduce
 * lato backend i due filtri che il gestionale applicava a ogni lettura — solo magazzini propri e
 * solo magazzini nella finestra di validita corrente (FR-036). Il filtro per tipologia resta
 * invece client-side, dov'era.
 *
 * Nessun ripiego sul gestionale: l'anagrafica locale e' l'unica sorgente. Un'organizzazione senza
 * import ha il selettore vuoto — e' voluto, perche' un fallback coprirebbe il caso innocuo
 * (zero magazzini) e non quello dannoso (import parziale, alcuni magazzini mancanti in silenzio).
 */
@Component({
  selector: 'app-magazzino-input',
  templateUrl: './magazzino-input.component.html',
  styleUrls: ['./magazzino-input.component.scss'],
  standalone: false
})
export class MagazzinoInputComponent {
  @Input() codMag = '';
  @Output() codMagChange = new EventEmitter<string>();
  @Input() codAreaMag = '';
  @Output() codAreaMagChange = new EventEmitter<string>();
  @Input() tipoMagazzino = -1;
  magazzini: MagazzinoLocale[] = [];
  aree: MagazzinoAreaLocale[] = [];
  showMagazzini = false;
  showAree = false;
  loading = false;
  // Ionic emette ionChange al blur se il valore è cambiato dopo l'ultimo commit:
  // la scansione riscrive codMag programmaticamente, quindi il blur successivo
  // scatenerebbe getMagazzini(). Il flag sopprime quel singolo ionChange.
  private suppressNextIonChange = false;
  constructor(
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage,
    private magazziniService: MagazziniService) { }


  getMagazzini = (fromIonChange = false) => {
    if (fromIonChange && this.suppressNextIonChange) {
      this.suppressNextIonChange = false;
      return false;
    }
    if (this.loading) {
      return false;
    }
    this.showMagazzini = !this.showMagazzini;
    if (!this.showMagazzini) {
      this.magazzini = [];
      return false;
    }
    this.loading = true;
    this.magazziniService.getMagazziniSelezionabili(this.organizzazioniService.organizzazioneId)
      .then((_magazzini: SearchResult<MagazzinoLocale>) => {
        if (_magazzini && _magazzini.totalCount > 0) {
          _magazzini.result.forEach((_mag: MagazzinoLocale) => {
            if (this.matchTipologia(_mag)) {
              this.magazzini.push(_mag);
            }
          });
          if (this.magazzini.filter(m => m.codice === this.codMag).length > 0) {
            this.codMagChange.emit(this.codMag);
          }
        }
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il caricamento dei magazzini."))
      .finally(() => this.loading = false);
  };

  // `tipoMagazzino === -1` = nessun filtro. Una voce con tipologia non valorizzata — tipico di un
  // magazzino creato a mano — compare quindi solo dove il chiamante non filtra: chi filtra chiede
  // una tipologia precisa, e attribuirgliene una a caso sarebbe peggio che non mostrarla.
  private matchTipologia = (magazzino: MagazzinoLocale): boolean =>
    this.tipoMagazzino === -1 || magazzino.tipologiaMagazzino === this.tipoMagazzino;

  resetMagazzino = () => {
    this.codMagChange.emit('');
    this.codAreaMagChange.emit('');
  };
  resetArea = () => this.codAreaMagChange.emit('');

  selectMagazzino = (magazzino: MagazzinoLocale) => {
    if (!magazzino || !magazzino.codice) {
      return false;
    }
    this.showMagazzini = false;
    this.magazzini = [];
    this.codMagChange.emit(magazzino.codice);
  };

  getAree = (codMag: string) => {
    if (this.loading) {
      return false;
    }
    this.showAree = !this.showAree;
    if (!this.showAree) {
      this.aree = [];
      return false;
    }
    this.loading = true;
    this.magazziniService.getAree(this.organizzazioniService.organizzazioneId, codMag)
      .then((_aree: SearchResult<MagazzinoAreaLocale>) => {
        if (_aree && _aree.totalCount > 0) {
          // Solo le aree attive: una disattivata in anagrafica non e' movimentabile.
          this.aree = _aree.result.filter(a => a.isActive);
          if (this.aree.filter(a => a.codice === this.codAreaMag).length > 0) {
            this.codAreaMagChange.emit(this.codAreaMag);
          }
        }
      })
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il caricamento delle aree."))
      .finally(() => this.loading = false);
  };

  selectArea = (area: MagazzinoAreaLocale) => {
    if (!area || !area.codice) {
      return false;
    }
    this.showAree = false;
    this.aree = [];
    this.codAreaMagChange.emit(area.codice);
  };

  onMagazzinoEnter = (): void => {
    if (this.loading) {
      return;
    }
    const parsed = this.parseScan(this.codMag);
    if (!parsed) {
      return;
    }
    this.validateScan(parsed.codMag, parsed.codArea);
  };

  private parseScan(raw: string): { codMag: string, codArea: string } | null {
    if (!raw || !raw.trim()) {
      return null;
    }
    const segments = raw.trim().split('|').map(s => s.trim());
    const codMag = segments[0];
    const codArea = segments[1] ?? '';
    if (!codMag) {
      this.saveMessage.messageError('Barcode non valido: codice magazzino mancante.');
      this.clearScan();
      return null;
    }
    return {codMag, codArea};
  }

  private clearScan(): void {
    this.suppressNextIonChange = true;
    this.codMag = '';
    this.codAreaMag = '';
    this.codMagChange.emit('');
    this.codAreaMagChange.emit('');
  }

  private async validateScan(codMagRaw: string, codAreaRaw: string): Promise<void> {
    this.loading = true;
    this.showMagazzini = false;
    this.showAree = false;
    this.magazzini = [];
    this.aree = [];
    try {
      const mag = await this.resolveMagazzino(codMagRaw);
      if (!mag) {
        this.saveMessage.messageError(`Magazzino "${codMagRaw}" non trovato.`);
        this.clearScan();
        return;
      }
      await this.applyResolvedScan(mag, codAreaRaw);
    } catch {
      this.saveMessage.messageError('Ci sono problemi con la verifica del magazzino.');
      this.clearScan();
    } finally {
      this.loading = false;
    }
  }

  private async applyResolvedScan(mag: MagazzinoLocale, codAreaRaw: string): Promise<void> {
    if (!codAreaRaw) {
      this.applyScan(mag, null);
      return;
    }
    const area = await this.resolveArea(mag.codice, codAreaRaw);
    if (!area) {
      this.saveMessage.messageError(`Area "${codAreaRaw}" non trovata nel magazzino "${mag.codice}".`);
      this.clearScan();
      return;
    }
    this.applyScan(mag, area);
  }

  private async resolveMagazzino(codMagRaw: string): Promise<MagazzinoLocale | null> {
    const _magazzini: SearchResult<MagazzinoLocale> =
      await this.magazziniService.getMagazziniSelezionabili(this.organizzazioniService.organizzazioneId);
    if (!_magazzini || _magazzini.totalCount <= 0) {
      return null;
    }
    const target = codMagRaw.toUpperCase();
    const match = _magazzini.result.find(m => m.codice.toUpperCase() === target && this.matchTipologia(m));
    return match ?? null;
  }

  private async resolveArea(codMag: string, codAreaRaw: string): Promise<MagazzinoAreaLocale | null> {
    const _aree: SearchResult<MagazzinoAreaLocale> =
      await this.magazziniService.getAree(this.organizzazioniService.organizzazioneId, codMag);
    if (!_aree || _aree.totalCount <= 0) {
      return null;
    }
    const target = codAreaRaw.toUpperCase();
    const match = _aree.result.find(a => a.isActive && a.codice.toUpperCase() === target);
    return match ?? null;
  }

  private applyScan(mag: MagazzinoLocale, area: MagazzinoAreaLocale | null): void {
    this.suppressNextIonChange = true;
    this.codMag = mag.codice;
    this.codMagChange.emit(mag.codice);
    if (!area) {
      this.codAreaMag = '';
      this.codAreaMagChange.emit('');
      return;
    }
    this.codAreaMag = area.codice;
    this.codAreaMagChange.emit(area.codice);
  }
}
